import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 30, 40, 50],
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (totalItems === 0) {
    return null;
  }

  return (
    <div className="mt-4 flex flex-col items-center gap-3 sm:flex-row sm:items-center sm:justify-between">
      {/* Total Items - Left */}
      <div className="order-2 text-center text-[12px] text-text-sub-600 sm:order-1 sm:text-left">
        Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{' '}
        results
      </div>

      {/* Page Navigation - Center */}
      <div className="order-1 flex flex-wrap items-center justify-center gap-1 sm:order-2">
        <button
          className="px-2 py-1 text-[12px] border border-stroke-sub-300 rounded-md hover:bg-bg-white-0 hover:text-text-strong-950 disabled:opacity-50"
          disabled={currentPage === 1}
          onClick={() => { if (currentPage > 1) onPageChange(1); }}
        >
          <i className="ri-arrow-left-double-line text-[10px]"></i>
        </button>
        <button
          className="px-2 py-1 text-[12px] border border-stroke-sub-300 rounded-md hover:bg-bg-white-0 hover:text-text-strong-950 disabled:opacity-50"
          disabled={currentPage === 1}
          onClick={() => { if (currentPage > 1) onPageChange(currentPage - 1); }}
        >
          <i className="ri-arrow-left-s-line text-[10px]"></i>
        </button>

        {(() => {
          const maxVisiblePages = 5;
          let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
          let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
          if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
          }
          const pages = [];
          for (let i = startPage; i <= endPage; i++) pages.push(i);

          return pages.map((page) => (
            <button
              key={page}
              className={`px-3 py-1 text-[12px] border rounded-md ${
                currentPage === page
                  ? 'border-primary bg-primary text-static-white'
                  : 'border-stroke-sub-300 text-text-strong-950 hover:bg-bg-weak-50'
              }`}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          ));
        })()}

        <button
          className="px-2 py-1 text-[12px] border border-stroke-sub-300 rounded-md hover:bg-bg-weak-50 disabled:opacity-50"
          disabled={currentPage === totalPages}
          onClick={() => { if (currentPage < totalPages) onPageChange(currentPage + 1); }}
        >
          <i className="ri-arrow-right-s-line text-[10px]"></i>
        </button>
        <button
          className="px-2 py-1 text-[12px] border border-stroke-sub-300 rounded-md hover:bg-bg-weak-50 disabled:opacity-50"
          disabled={currentPage === totalPages}
          onClick={() => { if (currentPage < totalPages) onPageChange(totalPages); }}
        >
          <i className="ri-arrow-right-double-line text-[10px]"></i>
        </button>
      </div>

      {/* Page Size Selector - Right */}
      <div className="order-3 flex items-center space-x-2">
        {onPageSizeChange && (
          <>
            <span className="text-[12px] text-text-sub-600">Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="text-[12px] border border-stroke-sub-300 rounded-md px-2 py-1 bg-bg-white-0 hover:border-stroke-strong-950 focus:outline-hidden focus:border-primary"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span className="text-[12px] text-text-sub-600">/ page</span>
          </>
        )}
      </div>
    </div>
  );
};

export default Pagination;
